/**
 * API client for the standalone Activations Admin API (a NEW service, distinct from the
 * CMS API). Mirrors the auth-token behaviour of `@/lib/queryClient` (Bearer token from
 * sessionStorage, 401/403 clears tokens) but targets a different base URL resolved from
 * `window.APP_CONFIG.ACTIVATIONS_API_BASE_URL` > `VITE_ACTIVATIONS_API_URL` > default.
 *
 * Admin endpoints require role ATHLYN_ADMIN or ATHLYN_TEAM (the same JWT the CMS portal
 * already holds). Once the Activations API is containerized + added to /swagger-gen the
 * response shapes here can be regenerated; the request/response envelope is identical to
 * the rest of the platform (ApiResponseHelper).
 */
import { ApiResponseWrapper, isErrorResponse } from '@/types/api';

const DEFAULT_ACTIVATIONS_API_URL = 'https://activations.api.dev.athlyn.app';

function getStoredAccessToken(): string | null {
  return sessionStorage.getItem('athlyn_cms_token');
}

function clearStoredTokens() {
  sessionStorage.removeItem('athlyn_cms_token');
  sessionStorage.removeItem('athlyn_cms_refresh');
  sessionStorage.removeItem('athlyn_cms_user');
  sessionStorage.removeItem('athlyn_cms_auth_method');
  sessionStorage.removeItem('athlyn_cms_microsoft_tokens');
}

export function getActivationsApiBaseUrl(): string {
  return (
    window.APP_CONFIG?.ACTIVATIONS_API_BASE_URL ||
    import.meta.env.VITE_ACTIVATIONS_API_URL ||
    DEFAULT_ACTIVATIONS_API_URL
  );
}

/**
 * Builds the BlazePod leaderboard display URL the operator opens on a nearby screen.
 *
 * There is no standalone constant for the participant-facing activations web base —
 * the QR derives the participant runner URL from the server (the short-code endpoint's
 * `runnerUrl`). The leaderboard lives on the SAME web app under the `/l/{shortCode}` path,
 * so we reuse the runner URL's origin and just swap in the `/l/` path. Falls back to a
 * relative `/l/{shortCode}` when no runner URL is available.
 */
export function buildLeaderboardUrl(
  runnerUrl: string | null | undefined,
  shortCode: string | null | undefined,
): string {
  const code = shortCode ?? '';
  if (runnerUrl) {
    try {
      const origin = new URL(runnerUrl).origin;
      return `${origin}/l/${code}`;
    } catch {
      // Fall through to the relative form on an unparseable runner URL.
    }
  }
  return `/l/${code}`;
}

async function parseResponse<T>(res: Response): Promise<ApiResponseWrapper<T>> {
  // 204 No Content (e.g. delete, session close) has no body.
  if (res.status === 204) {
    return { success: true, data: undefined as unknown as T } as ApiResponseWrapper<T>;
  }

  let jsonData: ApiResponseWrapper<T>;
  try {
    jsonData = (await res.json()) as ApiResponseWrapper<T>;
  } catch {
    const error = new Error('Invalid Activations API response: Unable to parse JSON');
    (error as any).status = res.status;
    throw error;
  }

  if (isErrorResponse(jsonData)) {
    if (res.status === 401 || res.status === 403) {
      clearStoredTokens();
    }
    const message = jsonData.error?.message || res.statusText || 'An error occurred';
    const error = new Error(message);
    (error as any).status = res.status;
    (error as any).errorCode = jsonData.error?.code;
    (error as any).errorDetails = jsonData.error?.details;
    (error as any).fullResponse = jsonData;
    throw error;
  }

  return jsonData;
}

/**
 * Issues a request against the Activations API and unwraps the standard envelope's `data`.
 * Use for both queries and mutations (matches the cms-portal `apiRequest` convention).
 */
export async function activationsApiRequest<T = unknown>(
  method: string,
  path: string,
  data?: unknown,
): Promise<T> {
  const accessToken = getStoredAccessToken();
  const baseUrl = getActivationsApiBaseUrl();
  const fullUrl = path.startsWith('http') ? path : `${baseUrl}${path}`;

  const headers: Record<string, string> = {};
  if (data !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });

  const response = await parseResponse<T>(res);
  const responseData = (response as any).data;
  return (responseData !== undefined ? responseData : (response as unknown)) as T;
}
